import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Gordios GetFilters response'undaki her bir filtre item'ı şu şekilde gelir:
 *  { code, name, hotelIds: number[], parentId? }
 * Kullanıcıya checkbox/listede "name" gösterilir, seçilen "code" Search'e gönderilir.
 */
export interface FilterItem {
  code: string | number;
  name: string;
  hotelIds?: number[];
  parentId?: number;
}

export interface HotelFilterRequest {
  checkInDate: string; // ISO
  checkOutDate: string;
  countryCode: string;
  destination: {
    id: number;
    label: string;
    parentId?: number | null;
  };
  rooms: {
    passengers: {
      age: number;
      passengerType: string; // "Adult" | "Child"
      gender: string; // "Male" | "Female" | "Unknown"
      name?: string;
      surname?: string;
    }[];
  }[];
  hotelFilterIncludeItems?: string[] | null;
}

export interface HotelFilterResult {
  // .NET WCF direkt array olarak serialize eder (wrapper objesi yok)
  categoryTypes?: FilterItem[];
  discountTypes?: FilterItem[];
  foodAndDrinks?: FilterItem[];
  hotelFacilities?: FilterItem[];
  hotelNames?: string[];
  hotelRoomFacilities?: FilterItem[];
  hotelThemes?: FilterItem[];
  hotelTypes?: FilterItem[];
  hygienicalMeasures?: FilterItem[];
  locations?: FilterItem[];
  mealTypes?: FilterItem[];
  nearDistances?: FilterItem[];
  campaignTypes?: FilterItem[];
  actionTypes?: FilterItem[];
  priceRanges?: { minPrice?: number; maxPrice?: number };
}

export interface HotelFilterResponse {
  success: boolean;
  traceId?: string;
  result?: HotelFilterResult;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class HotelFilterService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  private _filters$ = new BehaviorSubject<HotelFilterResult | null>(null);
  filters$ = this._filters$.asObservable();

  private _loading$ = new BehaviorSubject<boolean>(false);
  loading$ = this._loading$.asObservable();

  getFilters(req: HotelFilterRequest): Observable<HotelFilterResponse> {
    this._loading$.next(true);
    return this.http.post<HotelFilterResponse>(
      `${this.apiUrl}/HotelFilter/getFilters`,
      req
    ).pipe(
      tap(res => {
        if (res?.success && res.result) this._filters$.next(res.result);
        this._loading$.next(false);
      }),
      shareReplay(1)
    );
  }

  clear(): void {
    this._filters$.next(null);
  }

  /**
   * Gordios yanıtı .NET serialization'ı üzerinden direkt array olarak gelir.
   * Güvenlik için hem array hem wrapper obje formatını destekler.
   */
  static flatten<T>(value: any, wrapperKey?: string): T[] {
    if (!value) return [];
    if (Array.isArray(value)) return value as T[];
    if (wrapperKey && value[wrapperKey]) {
      const arr = value[wrapperKey];
      return Array.isArray(arr) ? (arr as T[]) : [arr as T];
    }
    return [];
  }
}
